import { lookup } from 'node:dns/promises'
import { connect, isIP, type Socket } from 'node:net'
import { networkInterfaces } from 'node:os'

/**
 * 地址是否指向**本机或本链路**：回环、未指定、链路本地（含云厂商的元数据地址
 * 169.254.169.254）、组播与广播、不在链路本地段里的云平台端点
 * （{@link PLATFORM_ENDPOINTS}），以及**本机网卡自己的地址**。
 *
 * 最后一项是故意的：服务绑在 `0.0.0.0` 时，局域网地址与回环等价，不拦就等于绕过了上一条。
 * 代价是「公司域名解析到我本机的开发服务」这类场景会被拦；规则逐字点名主机时不查这些，
 * 所以把具体地址加进允许表就能恢复。
 */
export function isLocalAddress(address: string, own: ReadonlySet<string> = ownAddresses()): boolean {
  const kind = isIP(address)
  if (kind === 4) return isLocalV4(address) || own.has(address)
  if (kind !== 6) return false
  // 先压缩成规范形态：SOCKS5 与 CONNECT 都可能送来展开的写法（0:0:0:0:0:0:0:1）。
  const value = canonicalV6(address)
  if (value === undefined) return true
  if (own.has(value)) return true
  const mapped = mappedV4(value)
  if (mapped !== undefined) return isLocalV4(mapped) || own.has(mapped)
  if (value === '::' || value === '::1') return true
  if (PLATFORM_ENDPOINT_V6.some(prefix => value.startsWith(prefix))) return true
  // fe80::/10 链路本地；ff00::/8 组播。
  return /^fe[89ab]/u.test(value) || value.startsWith('ff')
}

/** IPv6 的规范（压缩、小写）写法；读不懂就返回 `undefined`，调用方按本机处理（fail closed）。 */
function canonicalV6(address: string): string | undefined {
  try {
    return new URL(`http://[${address.replace(/%.*$/u, '')}]/`).hostname.slice(1, -1)
  } catch {
    return undefined
  }
}

/** `::ffff:a.b.c.d` 与 `::ffff:7f00:1` 两种写法的 IPv4 映射地址。 */
function mappedV4(value: string): string | undefined {
  const dotted = /^::ffff:(\d+\.\d+\.\d+\.\d+)$/u.exec(value)?.[1]
  if (dotted !== undefined) return dotted
  const hex = /^::ffff:([0-9a-f]{1,4}):([0-9a-f]{1,4})$/u.exec(value)
  if (hex === null) return undefined
  const high = Number.parseInt(hex[1] ?? '0', 16)
  const low = Number.parseInt(hex[2] ?? '0', 16)
  return [high >> 8, high & 255, low >> 8, low & 255].join('.')
}

/**
 * 云厂商的实例元数据与平台端点：不在 link-local 段里，所以上面的网段规则抓不到它们。
 * 拿到这些地址等于拿到这台机器的临时凭证，而命令行工具与 SDK 会直接打它们。
 */
const PLATFORM_ENDPOINTS = new Set([
  '100.100.100.200', // Alibaba Cloud
  '168.63.129.16', // Azure
  '192.0.0.192', // Oracle Cloud
])

/**
 * 同上，IPv6 形式。按 `/32` 前缀判，所以不拦整个 ULA 段（`fd00::1` 这类内网地址照旧），
 * 只拦这几个分配出去的块。
 */
const PLATFORM_ENDPOINT_V6 = ['fd00:ec2:', 'fd20:ce:', 'fd00:c1:', 'fd00:42:']

function isLocalV4(address: string): boolean {
  if (PLATFORM_ENDPOINTS.has(address)) return true
  const [a = 0, b = 0] = address.split('.').map(Number)
  return a === 127 || a === 0 || (a === 169 && b === 254) || a >= 224
}

/**
 * 本机网卡当前的地址（已规范化）。读一次网卡列表是系统调用，而一条命令会检查好几个答案，
 * 所以缓存一小会儿：接口出现/消失的延迟上限就是这段时间。
 */
const OWN_CACHE_MS = 1_000
let ownCache: { at: number; addresses: Set<string> } | undefined

export function ownAddresses(now = Date.now()): ReadonlySet<string> {
  if (ownCache !== undefined && now - ownCache.at < OWN_CACHE_MS) return ownCache.addresses
  const addresses = new Set<string>()
  for (const list of Object.values(networkInterfaces())) {
    for (const entry of list ?? []) {
      // 带 zone 的链路本地地址（fe80::1%eth0）按去掉 zone 的写法比较。
      const bare = entry.address.replace(/%.*$/u, '')
      const canonical = isIP(bare) === 6 ? canonicalV6(bare) : bare
      if (canonical !== undefined) addresses.add(canonical)
    }
  }
  ownCache = { at: now, addresses }
  return addresses
}

/** 名字解析到了本机地址，而允许它的规则没有点名它。 */
export class LocalAddressError extends Error {
  override name = 'LocalAddressError'

  constructor(readonly host: string, readonly address: string) {
    super(`${host} resolves to ${address}, a local address; list ${host} itself (not a wildcard) to allow it`)
  }
}

export interface DialOptions {
  /** 允许连本机地址：允许它的规则逐字点名了这个主机。 */
  allowLocal: boolean
  /** 替换 DNS（测试用）。 */
  resolve?: (host: string) => Promise<string[]>
  /** 替换「本机地址」集（测试用）；默认读网卡。 */
  ownAddresses?: () => ReadonlySet<string>
  timeoutMs?: number
}

export async function defaultResolve(host: string): Promise<string[]> {
  const answers = await lookup(host, { all: true, verbatim: true })
  return answers.map(answer => answer.address)
}

/**
 * 直连 `host:port`。先解析、按 {@link isLocalAddress} 检查**全部**答案，再连**已检查过**的
 * 地址 —— 而不是把名字交给 `connect` 再解析一次，否则 DNS 换答案（rebinding）就绕过了检查。
 * @throws LocalAddressError 任一答案是本机地址且 `allowLocal` 为假。
 */
export async function dialDirect(host: string, port: number, options: DialOptions): Promise<Socket> {
  const own = options.ownAddresses?.() ?? ownAddresses()
  const check = (address: string): boolean => isLocalAddress(address, own)
  const addresses = isIP(host) !== 0 ? [host] : await (options.resolve ?? defaultResolve)(host)
  if (addresses.length === 0) throw new Error(`${host} did not resolve to any address`)
  if (!options.allowLocal) {
    const local = addresses.find(check)
    if (local !== undefined) throw new LocalAddressError(host, local)
  }
  let lastError: unknown
  for (const address of addresses) {
    try {
      return await connectTo(address, port, options.timeoutMs ?? 30_000)
    } catch (error) {
      lastError = error
    }
  }
  throw lastError instanceof Error ? lastError : new Error(`cannot connect to ${host}:${port}`)
}

function connectTo(address: string, port: number, timeoutMs: number): Promise<Socket> {
  return new Promise((resolve, reject) => {
    const socket = connect({ host: address, port })
    const timer = setTimeout(() => {
      socket.destroy()
      reject(new Error(`connecting to ${address}:${port} timed out after ${timeoutMs} ms`))
    }, timeoutMs)
    socket.once('connect', () => {
      clearTimeout(timer)
      socket.removeListener('error', onError)
      resolve(socket)
    })
    const onError = (error: Error): void => {
      clearTimeout(timer)
      reject(error)
    }
    socket.once('error', onError)
  })
}
